import { useEffect, useState } from "react"
import { Link, NavLink, Outlet, useLocation } from "react-router-dom"
import { PageTransition } from "@/components/ui/page-transition"
import {
  LayoutDashboard,
  ShieldCheck,
  FileBarChart2,
  Flag,
} from "lucide-react"
import { ProctorHeader } from "@/components/layout/ProctorHeader"
import { useAuthStore } from "@/stores/auth"
import { BrandLogo } from "@/components/BrandLogo"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"

type ProctorNavItem = {
  to: string
  label: string
  icon: typeof LayoutDashboard
  // When true, render the live "flags today" count as a badge.
  showFlagsBadge?: boolean
}

const navItems: ProctorNavItem[] = [
  { to: "/proctor/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/proctor/authorize", label: "Authorize", icon: ShieldCheck },
  { to: "/proctor/flags", label: "Flags", icon: Flag, showFlagsBadge: true },
  { to: "/proctor/reports", label: "Reports", icon: FileBarChart2 },
]

function getInitials(input: string | undefined): string {
  if (!input) return "P"
  const parts = input.trim().split(/\s+/)
  if (parts.length === 0) return "P"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function ProctorLayout() {
  const user = useAuthStore((s) => s.user)
  const location = useLocation()

  // "Flags today" badge — polled on mount, route change, and every 60s.
  const [flagsToday, setFlagsToday] = useState(0)
  useEffect(() => {
    let cancelled = false
    const fetchCount = () => {
      api
        .get<{ data: { total: number; today: number } }>("/proctoring/flags/count")
        .then((res) => {
          if (!cancelled) setFlagsToday(res.data.data?.today ?? 0)
        })
        .catch(() => {
          /* non-fatal */
        })
    }
    fetchCount()
    const id = window.setInterval(fetchCount, 60_000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle)]">
      <aside className="sidebar-light fixed inset-y-0 left-0 z-30 w-64 border-r border-[var(--color-border)] flex flex-col">
        {/* Logo */}
        <div className="px-6 py-5">
          <Link to="/proctor/dashboard" className="font-display inline-flex items-center gap-2">
            <BrandLogo size="sidebar" />
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 pb-3 space-y-0.5 overflow-y-auto">
          {navItems.map(({ to, label, icon: Icon, showFlagsBadge }) => {
            const badge = showFlagsBadge && flagsToday > 0 ? flagsToday : null
            return (
              <NavLink
                key={to}
                to={to}
                end={false}
                className={({ isActive }) =>
                  cn(
                    "nav-item group flex items-center gap-3 rounded-full px-3.5 py-2 text-sm font-medium",
                    isActive
                      ? "is-active bg-[var(--color-accent)] text-white shadow-[var(--shadow-sm)]"
                      : "text-[color:var(--color-text-secondary)] hover:bg-[var(--color-accent-light)] hover:text-[color:var(--color-accent-hover)]",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      className={cn(
                        "nav-icon h-5 w-5 shrink-0 transition-colors",
                        isActive
                          ? "text-white"
                          : "text-[color:var(--color-text-muted)] group-hover:text-[color:var(--color-accent-hover)]",
                      )}
                      strokeWidth={1.5}
                    />
                    <span className="truncate">{label}</span>
                    {badge != null ? (
                      <span
                        className={cn(
                          "ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold tabular-nums",
                          isActive
                            ? "bg-white/20 text-white"
                            : "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
                        )}
                      >
                        {badge > 99 ? "99+" : badge}
                      </span>
                    ) : null}
                  </>
                )}
              </NavLink>
            )
          })}
        </nav>

        {/* User block — identity only. Sign-out + dark-mode now live in
            the header dropdown (ProctorHeader) so they aren't duplicated. */}
        <div className="px-3 pb-4 pt-3 border-t border-[var(--color-border)]">
          <div className="flex items-center gap-3 px-2 py-2">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
              style={{
                background: "var(--color-accent-light)",
                color: "var(--color-accent-hover)",
              }}
            >
              {getInitials(user?.fullName ?? user?.email)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-foreground">
                {user?.fullName ?? user?.email ?? "Proctor"}
              </div>
              <div className="truncate text-xs text-[color:var(--color-text-muted)]">
                {user?.email ?? "Live monitoring"}
              </div>
            </div>
          </div>
        </div>
      </aside>

      <main className="ml-64 min-h-screen">
        <ProctorHeader />
        <div className="p-8 max-w-7xl mx-auto">
          <PageTransition key={location.pathname}>
            <Outlet />
          </PageTransition>
        </div>
      </main>
    </div>
  )
}
